import { useState } from "react";
import api, { errMsg } from "@/lib/api";
import { Money } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { formatTanggal, nowLocalInput } from "@/lib/format";
import { toast } from "sonner";

const uuid = () => (crypto?.randomUUID ? crypto.randomUUID() : `pk-${Date.now()}-${Math.random()}`);
const pad = (n) => String(n).padStart(2, "0");
const toIso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const todayLocal = () => toIso(new Date());
export const monthStartLocal = () => { const d = new Date(); return toIso(new Date(d.getFullYear(), d.getMonth(), 1)); };
export const daysUntil = (iso) => {
  if (!iso) return null;
  const t = new Date(todayLocal() + "T00:00:00");
  return Math.round((new Date(String(iso).slice(0, 10) + "T00:00:00") - t) / 86400000);
};

// Mirrors backend add_months (clamps day to month end), returns inclusive last day.
export function endDateIso(startIso, months) {
  if (!startIso || months < 1) return "";
  const [y, m, d] = String(startIso).slice(0, 10).split("-").map(Number);
  const tm = m - 1 + months;
  const ty = y + Math.floor(tm / 12);
  const mo = tm % 12;
  const last = new Date(ty, mo + 1, 0).getDate();
  const end = new Date(ty, mo, Math.min(d, last));
  end.setDate(end.getDate() - 1);
  return toIso(end);
}

const MONTH_CHIPS = [1, 3, 6, 12];

function MonthsField({ value, onChange, testid }) {
  return (
    <div>
      <Label>Jumlah Bulan</Label>
      <div className="mt-1 flex items-center gap-2">
        <Input type="number" min="1" max="24" value={value} onChange={(e) => onChange(e.target.value)} className="w-20" data-testid={testid} />
        <div className="flex flex-wrap gap-1">
          {MONTH_CHIPS.map((m) => (
            <button key={m} type="button" onClick={() => onChange(String(m))}
              className={`rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${String(value) === String(m) ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-accent"}`}
              data-testid={`${testid}-chip-${m}`}>{m} bln</button>
          ))}
        </div>
      </div>
    </div>
  );
}

function PaymentFields({ f, setF }) {
  return (
    <>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div><Label>Metode</Label>
          <Select value={f.method} onValueChange={(v) => setF((s) => ({ ...s, method: v }))}>
            <SelectTrigger data-testid="pk-method"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="transfer">Transfer Bank</SelectItem><SelectItem value="cash">Tunai</SelectItem></SelectContent>
          </Select>
        </div>
        <div><Label>Waktu Pembayaran</Label><Input type="datetime-local" value={f.received_at} onChange={(e) => setF((s) => ({ ...s, received_at: e.target.value }))} data-testid="pk-received" /></div>
      </div>
      <div><Label>Catatan (opsional)</Label><Textarea value={f.note} onChange={(e) => setF((s) => ({ ...s, note: e.target.value }))} data-testid="pk-note" /></div>
    </>
  );
}

function Summary({ end, months, total }) {
  return (
    <div className="space-y-1 rounded-lg bg-accent/40 p-3 text-sm" data-testid="pk-summary">
      <div className="flex justify-between"><span className="text-muted-foreground">Berlaku s/d</span><span className="font-semibold" data-testid="pk-summary-end">{end ? formatTanggal(end) : "-"}</span></div>
      <div className="flex justify-between"><span className="text-muted-foreground">Lama sewa</span><span className="font-semibold">{months} bulan</span></div>
      <div className="flex justify-between"><span className="text-muted-foreground">Total Dibayar</span><span className="font-extrabold text-primary" data-testid="pk-summary-total"><Money value={total} /></span></div>
    </div>
  );
}

function validMoney(months, price) {
  if (months < 1 || months > 24) { toast.error("Jumlah bulan 1 sampai 24."); return false; }
  if (price <= 0) { toast.error("Harga per bulan wajib diisi."); return false; }
  return true;
}

export function SlotCountDialog({ mode, project, freeCount, onClose, onDone }) {
  const [count, setCount] = useState("1");
  const [loading, setLoading] = useState(false);
  const add = mode === "add";
  const save = async () => {
    const n = parseInt(count, 10) || 0;
    if (n < 1) { toast.error("Jumlah slot minimal 1."); return; }
    setLoading(true);
    try {
      const { data } = await api.post(add ? "/parking/slots" : "/parking/slots/reduce", { project_id: project.id, count: n });
      toast.success(add ? `${data.added.length} slot ditambahkan` : `${data.removed.length} slot dikurangi`, { description: (add ? data.added : data.removed).join(", ") });
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="pk-slot-count-dialog">
        <DialogHeader><DialogTitle>{add ? "Tambah Slot Parkir" : "Kurangi Slot Parkir"} — {project.name}</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <Label>Jumlah slot</Label>
          <Input type="number" min="1" max="100" value={count} onChange={(e) => setCount(e.target.value)} data-testid="pk-slot-count-input" />
          <p className="text-xs text-muted-foreground">
            {add ? "Slot baru diberi nomor otomatis (P-01, P-02, ...)." : `Hanya slot kosong yang dihapus, mulai dari nomor terbesar. Slot kosong saat ini: ${freeCount}.`}
          </p>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={save} disabled={loading} variant={add ? "default" : "destructive"} data-testid="pk-slot-count-save">{add ? "Tambah" : "Kurangi"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RentDialog({ slot, onClose, onDone }) {
  const [idem] = useState(uuid);
  const [f, setF] = useState({ renter_name: "", whatsapp: "", plate_number: "", vehicle_type: "motor", start_date: todayLocal(), months: "1", price_per_month: "", method: "transfer", received_at: nowLocalInput(), note: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const months = parseInt(f.months, 10) || 0;
  const price = Number(f.price_per_month) || 0;
  const save = async () => {
    if (!f.renter_name.trim()) { toast.error("Nama penyewa wajib diisi."); return; }
    if (!f.plate_number.trim()) { toast.error("Plat nomor wajib diisi."); return; }
    if (!validMoney(months, price)) return;
    setLoading(true);
    try {
      const { data } = await api.post("/parking/rentals", {
        slot_id: slot.id, renter_name: f.renter_name, whatsapp: f.whatsapp, plate_number: f.plate_number, vehicle_type: f.vehicle_type,
        start_date: f.start_date, months, price_per_month: price, method: f.method, received_at: new Date(f.received_at).toISOString(), note: f.note, idempotency_key: idem,
      });
      toast.success("Sewa parkir dicatat.", { description: `Struk ${data.receipt_number}` }); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="pk-rent-dialog">
        <DialogHeader><DialogTitle>Sewakan Slot {slot.code} — {slot.project_name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div><Label>Nama Penyewa</Label><Input value={f.renter_name} onChange={set("renter_name")} data-testid="pk-rent-name" /></div>
            <div><Label>WhatsApp (opsional)</Label><Input value={f.whatsapp} onChange={set("whatsapp")} data-testid="pk-rent-wa" /></div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div><Label>Plat Nomor</Label><Input value={f.plate_number} onChange={set("plate_number")} placeholder="B 1234 XYZ" className="uppercase" data-testid="pk-rent-plate" /></div>
            <div><Label>Kendaraan</Label>
              <Select value={f.vehicle_type} onValueChange={(v) => setF((s) => ({ ...s, vehicle_type: v }))}>
                <SelectTrigger data-testid="pk-rent-vehicle"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="motor">Motor</SelectItem><SelectItem value="mobil">Mobil</SelectItem><SelectItem value="lainnya">Lainnya</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div><Label>Mulai</Label><Input type="date" value={f.start_date} onChange={set("start_date")} data-testid="pk-rent-start" /></div>
            <div><Label>Harga per Bulan (Rp)</Label><Input type="number" min="1" value={f.price_per_month} onChange={set("price_per_month")} placeholder="Isi harga" data-testid="pk-rent-price" /></div>
          </div>
          <MonthsField value={f.months} onChange={(v) => setF((s) => ({ ...s, months: v }))} testid="pk-rent-months" />
          <PaymentFields f={f} setF={setF} />
          <Summary end={endDateIso(f.start_date, months)} months={months} total={months * price} />
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={loading} data-testid="pk-rent-save">Simpan & Buat Struk</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ExtendDialog({ rental, onClose, onDone }) {
  const [idem] = useState(uuid);
  const [f, setF] = useState({ months: "1", price_per_month: rental.price_per_month ? String(rental.price_per_month) : "", method: "transfer", received_at: nowLocalInput(), note: "" });
  const [loading, setLoading] = useState(false);
  const months = parseInt(f.months, 10) || 0;
  const price = Number(f.price_per_month) || 0;
  const start = String(rental.end_exclusive || "").slice(0, 10);
  const save = async () => {
    if (!validMoney(months, price)) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/parking/rentals/${rental.id}/extend`, {
        months, price_per_month: price, method: f.method, received_at: new Date(f.received_at).toISOString(), note: f.note, idempotency_key: idem,
      });
      toast.success("Sewa parkir diperpanjang.", { description: `Struk ${data.receipt_number}` }); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="pk-extend-dialog">
        <DialogHeader><DialogTitle>Perpanjang Sewa Parkir — Slot {rental.slot_code}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg border border-border p-3 text-sm" data-testid="pk-extend-renter">
            <div className="font-semibold">{rental.renter_name} · {rental.plate_number}</div>
            <div className="text-xs text-muted-foreground">Berlaku saat ini s/d {formatTanggal(rental.end_date)}</div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <MonthsField value={f.months} onChange={(v) => setF((s) => ({ ...s, months: v }))} testid="pk-extend-months" />
            <div><Label>Harga per Bulan (Rp)</Label><Input type="number" min="1" value={f.price_per_month} onChange={(e) => setF((s) => ({ ...s, price_per_month: e.target.value }))} placeholder="Isi harga" className="mt-1" data-testid="pk-extend-price" /></div>
          </div>
          <PaymentFields f={f} setF={setF} />
          <Summary end={endDateIso(start, months)} months={months} total={months * price} />
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={loading} data-testid="pk-extend-save">Simpan & Buat Struk</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
